import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Form, 
  Input, 
  Select, 
  Button, 
  Space, 
  Card, 
  Col, 
  message,
  DatePicker,
  Tag,
  Drawer,
  Descriptions,
  Spin,
  Divider,
  Image
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import { useNavigate } from 'react-router-dom';
import { ExportOutlined, LoadingOutlined, EnvironmentOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import FilterPanel from '../../../../components/common/FilterPanel';
import * as orderService from '../../../../services/order/orderService';
import { ORDER_STATUS_OPTIONS, PAYMENT_METHOD_OPTIONS } from '../../../../constants/order';

const { RangePicker } = DatePicker;

/**
 * 退货订单列表页
 *路由: /order/return-order/list
 */
const ReturnOrderList = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { tableWrapRef, scrollY } = useTableScrollHeight();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`
  });

  // 下拉选项（从后端数据动态获取）
  const [orderTypeOptions, setOrderTypeOptions] = useState([]);
  const [channelOptions, setChannelOptions] = useState([]);
  const [paymentTypeOptions, setPaymentTypeOptions] = useState([]);

  // Drawer状态管理
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  // 打开详情Drawer
  const handleViewDetail = async (orderNo) => {
    setDrawerVisible(true);
    setDrawerLoading(true);
    try {
      const response = await orderService.getReturnOrderByOrderNo(orderNo);
      if (response.success) {
        setCurrentOrder(response.data);
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('查询退货订单详情失败:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setDrawerLoading(false);
    }
  };

  // 关闭Drawer
  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setCurrentOrder(null);
  };

  //表格列定义
  const columns = [
    {
      title: t('order.returnOrder.list.table.orderNo'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      fixed: 'left',
      width: 180,
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => handleViewDetail(record.orderNo)}
          style={{ padding: 0 }}
        >
          {text}
        </Button>
      )
    },
    {
      title: t('order.returnOrder.list.table.orderType'),
      dataIndex: 'orderType',
      key: 'orderType',
      width: 100,
      render: (orderType) => {
        const option = orderTypeOptions.find(opt => opt.value === orderType);
        return (
          <span style={{ fontWeight: '500' }}>
            {option?.label || orderType}
          </span>
        );
      }
    },
    {
      title: t('order.returnOrder.list.table.channel'),
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      render: (channel) => {
        const option = channelOptions.find(opt => opt.value === channel);
        return option?.label || channel;
      }
    },
    {
      title: t('order.returnOrder.list.table.paymentType'),
      dataIndex: 'paymentType',
      key: 'paymentType',
      width: 120,
      render: (paymentType) => {
        const option = paymentTypeOptions.find(opt => opt.value === paymentType);
        return option?.label || paymentType || '-';
      }
    },
    {
      title: t('order.returnOrder.list.table.orderStatus'),
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      width: 100,
      render: (status) => {
        const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
        const colorMap = {
          'CREATED': 'blue',
          'IN_PROGRESS': 'processing',
          'COMPLETED': 'green',
          'CANCELLED': 'default',
          'RETURN': 'red'
        };
        return (
          <span style={{ 
            color: colorMap[status] === 'blue' ? '#1890ff' : 
                  colorMap[status] === 'green' ? '#52c41a' : 
                  colorMap[status] === 'red' ? '#ff4d4f' : '#8c8c8c',
            fontWeight: '500'
          }}>
            {t(option?.label || status)}
          </span>
        );
      }
    },
    {
      title: t('order.returnOrder.list.table.buyerName'),
      dataIndex: 'buyerName',
      key: 'buyerName',
      width: 120,
    },
    {
      title: t('order.returnOrder.list.table.skuQty'),
      dataIndex: 'skuQty',
      key: 'skuQty',
      width: 80,
      align: 'center'
    },
    {
      title: t('order.returnOrder.list.table.totalMu'),
      dataIndex: 'totalMu',
      key: 'totalMu',
      width: 80,
      align: 'center'
    },
    {
      title: t('order.returnOrder.list.table.totalAmount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      align: 'right',
      render: (amount) => `฿${amount?.toFixed(3) || '0.000'}`
    },
    {
      title: t('order.returnOrder.list.table.arrivalConfirmed'),
      dataIndex: 'arrivalConfirmed',
      key: 'arrivalConfirmed',
      width: 150,
      render: (arrivalConfirmed) => (
        <Tag color={arrivalConfirmed ? 'green' : 'default'}>
          {arrivalConfirmed ? t('common.yes') : t('common.no')}
        </Tag>
      )
    },
    {
      title: t('order.returnOrder.list.table.arrivalConfirmTime'),
      dataIndex: 'arrivalConfirmTime',
      key: 'arrivalConfirmTime',
      width: 180,
      render: (time) => time ? dayjs(time).format('DD-MM-YYYY HH:mm:ss') : '-'
    },
    {
      title: t('order.returnOrder.list.table.arrivalConfirmBy'),
      dataIndex: 'arrivalConfirmBy',
      key: 'arrivalConfirmBy',
      width: 120,
    },
    {
      title: t('order.returnOrder.list.table.confirmTime'),
      dataIndex: 'confirmTime',
      key: 'confirmTime',
      width: 180,
      render: (time) => time ? dayjs(time).format('DD-MM-YYYY HH:mm:ss') : '-'
    },
    {
      title: t('order.returnOrder.list.table.createTime'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      render: (createTime) => dayjs(createTime).format('DD-MM-YYYY HH:mm:ss')
    },
    {
      title: t('order.returnOrder.list.table.actions'),
      key: 'actions',
      fixed: 'right',
      width: 80,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            size="small"
            onClick={() => handleViewDetail(record.orderNo)}
          >
            {t('order.returnOrder.list.actions.view')}
          </Button>
        </Space>
      )
    }
  ];

  // 获取数据
  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const searchParams = {
        ...form.getFieldsValue(),
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        ...params
      };

      //处理时间范围
      if (searchParams.createTime && searchParams.createTime.length === 2) {
        searchParams.createTimeStart = searchParams.createTime[0].valueOf();
        searchParams.createTimeEnd = searchParams.createTime[1].valueOf();
        delete searchParams.createTime;
      }

      const response = await orderService.pageReturnOrder(searchParams);

      if (response.success) {
        const data = response.data || {};
        setDataSource(data.tableData || []);
        setPagination(prev => ({
          ...prev,
          current: data.paging?.currentPage || 1,
          total: data.paging?.totalCount || 0
        }));
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('查询退货订单失败:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  //初始加载数据 + 下拉选项
  useEffect(() => {
    fetchData({});
    orderService.getReturnOrderOptions().then(res => {
      if (res.success && res.data) {
        setOrderTypeOptions((res.data.orderTypes || []).map(o => ({ value: o.value, label: o.label })));
        setChannelOptions((res.data.channels || []).map(o => ({ value: o.value, label: o.label })));
        setPaymentTypeOptions((res.data.paymentTypes || []).map(o => ({ value: o.value, label: o.label })));
      }
    });
  }, []);

  //处理搜索
  const handleSearch = (values) => {
    form.setFieldsValue(values);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchData({ currentPage: 1 });
  };

  // 导出
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const searchParams = { ...form.getFieldsValue() };
      // 处理时间范围
      if (searchParams.createTime && searchParams.createTime.length === 2) {
        searchParams.createTimeStart = searchParams.createTime[0].valueOf();
        searchParams.createTimeEnd = searchParams.createTime[1].valueOf();
        delete searchParams.createTime;
      }
      message.loading(t('common.message.exportingData') || 'Exporting...', 0);
      const response = await orderService.exportReturnOrder(searchParams);
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = response.data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.destroy();
        message.success(t('common.message.exportSuccess') || 'Export successful');
      } else {
        message.destroy();
        message.error(response.errorMessage || t('common.message.exportFailed') || 'Export failed');
      }
    } catch (error) {
      message.destroy();
      console.error('导出退货订单失败:', error);
      message.error(t('common.message.exportFailed') || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  // 分页变化
  const handleTableChange = (paginationConfig) => {
    const newPagination = {
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    };
    setPagination(newPagination);
    fetchData({
      currentPage: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    });
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 搜索区域 */}
      <FilterPanel
        form={form}
        onFilter={handleSearch}
        onReset={handleReset}
        title={t('order.returnOrder.list.title')}
        collapsible={true}
        defaultCollapsed={false}
        loading={loading}
      >
        <Col span={6}>
          <Form.Item 
            name="orderNo" 
            label={t('order.returnOrder.list.search.orderNo')}
          >
            <Input placeholder={t('order.returnOrder.list.search.placeholder.orderNo')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="orderType" 
            label={t('order.returnOrder.list.search.orderType')}
          >
            <Select
              placeholder={t('order.returnOrder.list.search.placeholder.orderType')}
              options={orderTypeOptions}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="channel" 
            label={t('order.returnOrder.list.search.channel')}
          >
            <Select
              placeholder={t('order.returnOrder.list.search.placeholder.channel')}
              options={channelOptions}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="paymentType" 
            label={t('order.returnOrder.list.search.paymentType')}
          >
            <Select
              placeholder={t('order.returnOrder.list.search.placeholder.paymentType')}
              options={paymentTypeOptions}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item 
            name="arrivalConfirmed" 
            label={t('order.returnOrder.list.search.arrivalConfirmed')}
          >
            <Select 
              placeholder={t('order.returnOrder.list.search.placeholder.arrivalConfirmed')}
              options={[
                { value: true, label: t('common.yes') },
                { value: false, label: t('common.no') }
              ]}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item 
            name="createTime" 
            label={t('order.returnOrder.list.search.createTime')}
          >
            <RangePicker 
              showTime 
              format="DD-MM-YYYY HH:mm:ss"
              placeholder={[
                t('common.pleaseInput'),
                t('common.pleaseInput')
              ]}
            />
          </Form.Item>
        </Col>
      </FilterPanel>

      {/* 操作栏 */}
      <div style={{ marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
        <Button
          icon={exportLoading ? <LoadingOutlined /> : <ExportOutlined />}
          onClick={handleExport}
          loading={exportLoading}
        >
          {t('common.export')}
        </Button>
      </div>

      {/* 数据表格 */}
      <Card style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Table
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey="orderNo"
            scroll={{ x: 2000, y: scrollY }}
          />
        </div>
      </Card>

      {/* 订单详情Drawer */}
      <Drawer
        title={t('order.order.detail.title')}
        placement="right"
        width={960}
        open={drawerVisible}
        onClose={handleCloseDrawer}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={handleCloseDrawer}>
              {t('common.cancel')}
            </Button>
          </div>
        }
      >
        <Spin spinning={drawerLoading}>
          {currentOrder && currentOrder.order && (
            <>
              {/* 基本信息 */}
              <Descriptions 
                title={t('order.returnOrder.detail.basicInfo')} 
                bordered 
                column={2}
                size="small"
              >
                <Descriptions.Item label={t('order.order.detail.basic.orderNo')}>
                  {currentOrder.order.orderNo}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.order.detail.basic.upstreamOrderNo')}>
                  {currentOrder.order.upstreamOrderNo || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.order.detail.basic.orderType')}>
                  {(() => {
                    const option = orderTypeOptions.find(opt => opt.value === currentOrder.order.orderType);
                    return option?.label || currentOrder.order.orderType;
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.order.detail.basic.channel')}>
                  {(() => {
                    const option = channelOptions.find(opt => opt.value === currentOrder.order.channel);
                    return option?.label || currentOrder.order.channel || '-';
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.order.detail.basic.paymentType')}>
                  {(() => {
                    const option = paymentTypeOptions.find(opt => opt.value === currentOrder.order.paymentType);
                    return option?.label || currentOrder.order.paymentType || '-';
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.order.detail.basic.orderStatus')}>
                  {(() => {
                    const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === currentOrder.order.orderStatus);
                    return t(option?.label || currentOrder.order.orderStatus);
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.order.detail.basic.paymentMethod')}>
                  {(() => {
                    const option = PAYMENT_METHOD_OPTIONS.find(opt => opt.value === currentOrder.order.paymentMethod);
                    return t(option?.label || currentOrder.order.paymentMethod) || '-';
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.order.detail.basic.skuQty')}>
                  {currentOrder.order.skuQty}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.order.detail.basic.totalMu')}>
                  {currentOrder.order.totalMu}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.order.detail.basic.totalAmount')}>
                  ฿{currentOrder.order.totalAmount?.toFixed(3) || '0.000'}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.order.detail.basic.createTime')}>
                  {currentOrder.order.createTime ? dayjs(currentOrder.order.createTime).format('DD-MM-YYYY HH:mm:ss') : '-'}
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              {/* 退货信息 */}
              <Descriptions 
                title={t('order.returnOrder.detail.returnInfo')} 
                bordered 
                column={2}
                size="small"
              >
                <Descriptions.Item label={t('order.returnOrder.list.table.arrivalConfirmed')}>
                  <Tag color={currentOrder.order.arrivalConfirmed ? 'green' : 'default'}>
                    {currentOrder.order.arrivalConfirmed ? t('common.yes') : t('common.no')}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('order.returnOrder.list.table.arrivalConfirmTime')}>
                  {currentOrder.order.arrivalConfirmTime ? dayjs(currentOrder.order.arrivalConfirmTime).format('DD-MM-YYYY HH:mm:ss') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('order.returnOrder.list.table.arrivalConfirmBy')}>
                  {currentOrder.order.arrivalConfirmBy || '-'}
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              {/* 配送信息 */}
              {(() => {
                let delivery = null;
                try {
                  delivery = currentOrder.order.deliveryAddress
                    ? (typeof currentOrder.order.deliveryAddress === 'string'
                      ? JSON.parse(currentOrder.order.deliveryAddress)
                      : currentOrder.order.deliveryAddress)
                    : null;
                } catch (e) {
                  delivery = { addressLine1: currentOrder.order.deliveryAddress };
                }
                return (
                  <Descriptions 
                    title={t('order.order.detail.delivery.title')} 
                    bordered 
                    column={4}
                    size="small"
                  >
                    <Descriptions.Item label={t('order.order.detail.delivery.recipientName')}>
                      {delivery ? [delivery.firstName, delivery.lastName].filter(Boolean).join(' ') || currentOrder.order.buyerName || '-' : currentOrder.order.buyerName || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('order.order.detail.delivery.phoneNumber')}>
                      {delivery?.mobileNo || currentOrder.order.buyerPhone || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('order.order.detail.delivery.note')}>
                      {delivery?.deliveryNote || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('order.order.detail.delivery.address')}>
                      {[delivery?.addressLine1, delivery?.addressLine2, delivery?.addressLine3].filter(Boolean).join(', ') || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('order.order.detail.delivery.subDistrict')}>
                      {delivery?.subDistrict || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('order.order.detail.delivery.district')}>
                      {delivery?.city || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('order.order.detail.delivery.province')}>
                      {delivery?.state || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('order.order.detail.delivery.country')}>
                      {delivery?.country || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('order.order.detail.delivery.postcode')}>
                      {delivery?.postalCode || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('order.order.detail.delivery.latLng')} span={3}>
                      {delivery?.latitude && delivery?.longitude ? (
                        <a
                          href={`https://www.google.com/maps?q=${delivery.latitude},${delivery.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#13c2c2' }}
                        >
                          <EnvironmentOutlined style={{ marginRight: 4 }} />
                          {delivery.latitude}, {delivery.longitude}
                        </a>
                      ) : '-'}
                    </Descriptions.Item>
                  </Descriptions>
                );
              })()}

              <Divider />

              {/* 商品明细 */}
              <div>
                <h4 style={{ marginBottom: 16 }}>{t('order.returnOrder.detail.items.title')}</h4>
                <Table
                  dataSource={currentOrder.order.items || []}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: t('order.returnOrder.detail.items.itemCode'),
                      dataIndex: 'itemCode',
                      width: 120
                    },
                    {
                      title: t('order.returnOrder.detail.items.itemName'),
                      dataIndex: 'itemName',
                      width: 220,
                      render: (text, record) => (
                        <Space size={4} wrap>
                          <span>{text}</span>
                          {record.isSubstituted ? (
                            <Tag color="orange" style={{ marginLeft: 0 }}>
                              {t('order.returnOrder.detail.items.substituteTag')}
                            </Tag>
                          ) : null}
                        </Space>
                      )
                    },
                    {
                      title: t('order.returnOrder.detail.items.originalSku'),
                      dataIndex: 'originalSku',
                      width: 120,
                      render: (val, record) => (record.isSubstituted ? (val || '-') : '-')
                    },
                    {
                      title: t('order.returnOrder.detail.items.image'),
                      dataIndex: 'imageUrl',
                      key: 'imageUrl',
                      width: 80,
                      render: (url) => url ? (
                        <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==" />
                      ) : '-',
                    },
                    {
                      title: t('order.returnOrder.detail.items.isWeighted'),
                      dataIndex: 'isWeighted',
                      width: 80,
                      render: (val) => val ? t('common.yes') : t('common.no')
                    },
                    {
                      title: t('order.returnOrder.detail.items.pickedQty'),
                      dataIndex: 'actualPickedQty',
                      width: 100,
                      align: 'center',
                      render: (val) => val ?? '-'
                    },
                    {
                      title: t('order.returnOrder.detail.items.returnQty'),
                      dataIndex: 'returnQty',
                      width: 100,
                      align: 'center',
                      render: (val) => val ?? '-'
                    },
                    {
                      title: t('order.returnOrder.detail.items.diffQty'),
                      dataIndex: 'returnQty',
                      width: 100,
                      align: 'center',
                      render: (val, record) => {
                        const pickedQty = record.actualPickedQty || 0;
                        const returnQty = val || 0;
                        const diffQty = pickedQty - returnQty;
                        const hasDifference = diffQty !== 0;
                        return (
                          <span style={{ color: hasDifference ? '#ff4d4f' : 'inherit', fontWeight: hasDifference ? '500' : 'normal' }}>
                            {diffQty}
                          </span>
                        );
                      }
                    },
                    {
                      title: t('order.returnOrder.detail.items.unitPrice'),
                      dataIndex: 'unitPrice',
                      width: 100,
                      align: 'right',
                      render: (val) => `฿${val?.toFixed(3) || '0.000'}`
                    },
                    {
                      title: t('order.returnOrder.detail.items.lineAmount'),
                      dataIndex: 'lineAmount',
                      width: 100,
                      align: 'right',
                      render: (val) => `฿${val?.toFixed(3) || '0.000'}`
                    },
                    {
                      title: t('order.returnOrder.detail.items.diffAmount'),
                      dataIndex: 'returnQty',
                      width: 120,
                      align: 'right',
                      render: (val, record) => {
                        const pickedQty = record.actualPickedQty || 0;
                        const returnQty = val || 0;
                        const diffQty = pickedQty - returnQty;
                        const unitPrice = record.unitPrice || 0;
                        const diffAmount = diffQty * unitPrice;
                        const hasDifference = diffAmount !== 0;
                        return (
                          <span style={{ color: hasDifference ? '#ff4d4f' : 'inherit', fontWeight: hasDifference ? '500' : 'normal' }}>
                            ฿{diffAmount.toFixed(3)}
                          </span>
                        );
                      }
                    }
                  ]}
                />
              </div>
            </>
          )}
        </Spin>
      </Drawer>
    </div>
  );
};

export default ReturnOrderList;